import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { ArrowRightIcon, ArrowLeftIcon, EyeIcon } from '@heroicons/react/24/outline';
import PageLayout from '../components/PageLayout';
import PageBanner from '../components/PageBanner';
import SEO from '../components/SEO';
import API from '../api';
import { useLanguage } from '../context/LanguageContext';

const BASE = process.env.REACT_APP_API_URL?.replace('/api', '') ?? 'http://localhost:5000';

export default function CategoryBrandProducts() {
  const { t } = useLanguage();
  const { categorySlug, brandSlug } = useParams();
  const [products, setProducts] = useState([]);
  const [category, setCategory] = useState(null);
  const [brand, setBrand] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    Promise.all([
      API.get('/categories'),
      API.get('/brands'),
      API.get(`/products?category=${categorySlug}&brand=${brandSlug}`),
    ]).then(([cats, brs, prods]) => {
      setCategory(cats.data.find(c => c.slug === categorySlug) || null);
      setBrand(brs.data.find(b => b.slug === brandSlug) || null);
      setProducts(prods.data);
    }).catch(() => {}).finally(() => setLoading(false));
  }, [categorySlug, brandSlug]);

  const imgSrc = (url) => url?.startsWith('/') ? `${BASE}${url}` : url;

  return (
    <PageLayout>
      <SEO
        title={brand?.name ? `${brand.name} — ${category?.name || ''}` : undefined}
        description={brand?.description || (category?.name ? `${brand?.name || ''} products in ${category.name} from Sankyo Machine Tools.` : undefined)}
      />
      <PageBanner en="OUR PRODUCTS"
        title={brand?.name || t('common.products')}
        breadcrumbs={[
          { label: t('common.products'), to: '/products' },
          { label: category?.name || categorySlug, to: `/products/${categorySlug}` },
          { label: brand?.name || brandSlug },
        ]}
      />

      <section className="pt-6 pb-20 lg:pb-28 bg-white">
        <div className="w-[90%] max-w-7xl mx-auto">
          <Link to={`/products/${categorySlug}`}
            className="inline-flex items-center gap-1.5 text-sm text-gray-500 hover:text-primary transition-colors mb-8">
            <ArrowLeftIcon className="w-3.5 h-3.5" /> {t('common.backTo')} {category?.name || t('common.brands')}
          </Link>

          {brand?.description && (
            <div className="mb-10 max-w-2xl">
              <p className="text-gray-600">{brand.description}</p>
            </div>
          )}

          {loading ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
              {Array.from({ length: 8 }).map((_, i) => <div key={i} className="h-56 bg-gray-100 animate-pulse" />)}
            </div>
          ) : products.length === 0 ? (
            <div className="text-center py-20">
              <p className="text-gray-400 text-lg mb-2">{t('products.noProducts')}</p>
              <p className="text-gray-400 text-sm mb-6">{t('common.pleaseContact')}</p>
              <Link to="/contact" className="btn-primary text-sm">{t('common.contactUs')}</Link>
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
              {products.map((p, i) => (
                <Link key={p.id} to={`/products/${categorySlug}/${brandSlug}/${p.slug}`}
                  className="card fade-up group block" style={{ animationDelay: `${i * 0.08}s` }}>
                  <div className="h-44 bg-gray-100 overflow-hidden relative">
                    {p.image_url ? (
                      <img src={imgSrc(p.image_url)} alt={p.name}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-primary/10 to-gray-100">
                        <span className="text-primary/30 text-5xl font-display font-bold">{p.name[0]}</span>
                      </div>
                    )}
                    {p.unique_view_count > 0 && (
                      <div className="absolute top-2.5 right-2.5 flex items-center gap-1 bg-black/50 text-white text-xs font-medium px-2 py-0.5 rounded-full backdrop-blur-sm">
                        <EyeIcon className="w-3 h-3" />
                        {p.unique_view_count}
                      </div>
                    )}
                  </div>
                  <div className="p-4">
                    {p.brand_name && <span className="text-xs text-accent font-semibold uppercase tracking-wider">{p.brand_name}</span>}
                    <h3 className="font-bold text-ink mt-1 mb-1 text-sm leading-tight">{p.name}</h3>
                    {p.description && <p className="text-xs text-gray-500 line-clamp-2 mb-3">{p.description}</p>}
                    <span className="flex items-center gap-1.5 text-xs text-primary group-hover:text-primary transition-colors font-medium mt-2">
                      {t('common.viewDetails')} <ArrowRightIcon className="w-3.5 h-3.5 group-hover:translate-x-1 transition-transform" />
                    </span>
                  </div>
                </Link>
              ))}
            </div>
          )}

          {/* Enquiry CTA */}
          <div className="mt-20 bg-[#f5f5f5] p-10 text-center">
            <h3 className="text-xl font-bold text-ink mb-2">{t('common.cantFindTitle')}</h3>
            <p className="text-gray-600 text-sm mb-5">{t('common.cantFindSubtitle')}</p>
            <Link to="/contact" className="btn-primary btn-arrow text-sm">{t('common.contactUs')}</Link>
          </div>
        </div>
      </section>
    </PageLayout>
  );
}
